import { NX, NY, WAYPOINTS } from '../data/balance.js';
import type { Cell, GridPoint } from './types.js';

/**
 * Breadth-first search from (sx,sy) to (tx,ty) over the maze grid.
 * Neighbor order (+x, -x, +y, -y) matches the prototype exactly -- the chosen
 * path (and therefore creep movement and every downstream hash) depends on it.
 */
export function bfs(
  grid: readonly (readonly (Cell | null)[])[],
  sx: number,
  sy: number,
  tx: number,
  ty: number,
): GridPoint[] | null {
  const prev = new Int32Array(NX * NY).fill(-1);
  const seen = new Uint8Array(NX * NY);
  const q = [sy * NX + sx];
  seen[sy * NX + sx] = 1;
  const DX = [1, -1, 0, 0] as const;
  const DY = [0, 0, 1, -1] as const;
  for (let qi = 0; qi < q.length; qi++) {
    const cur = q[qi]!;
    const cx = cur % NX;
    const cy = (cur - cx) / NX;
    if (cx === tx && cy === ty) {
      const path: GridPoint[] = [];
      let c = cur;
      while (c !== -1) {
        path.push({ x: c % NX, y: Math.floor(c / NX) });
        c = prev[c]!;
      }
      return path.reverse();
    }
    for (let d = 0; d < 4; d++) {
      const nx = cx + DX[d]!;
      const ny = cy + DY[d]!;
      if (nx < 0 || ny < 0 || nx >= NX || ny >= NY) continue;
      const ni = ny * NX + nx;
      if (seen[ni]) continue;
      if (grid[ny]![nx] !== null && !(nx === tx && ny === ty)) continue;
      seen[ni] = 1;
      prev[ni] = cur;
      q.push(ni);
    }
  }
  return null;
}

/**
 * The full ground-creep path through all waypoints (IN -> 1 -> 2 -> 3 -> 4 -> OUT),
 * or null if the maze fully blocks any leg.
 */
export function computePath(grid: readonly (readonly (Cell | null)[])[]): GridPoint[] | null {
  const full: GridPoint[] = [];
  for (let i = 0; i < WAYPOINTS.length - 1; i++) {
    const a = WAYPOINTS[i]!;
    const b = WAYPOINTS[i + 1]!;
    const leg = bfs(grid, a.x, a.y, b.x, b.y);
    if (!leg) return null;
    for (let j = i === 0 ? 0 : 1; j < leg.length; j++) full.push(leg[j]!);
  }
  return full;
}
